import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MOBILE_DEFAULT_WINDOW_SECONDS = 90;
const MIN_VISIBLE_DURATION_SECONDS = 10;
const AUTO_FOLLOW_IDLE_MS = 1600;
const AUTO_FOLLOW_PADDING_RATIO = 0.3;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const isFinitePositive = (value: number): boolean => Number.isFinite(value) && value > 0;

const getViewportDuration = (
  totalDuration: number,
  minVisibleDuration: number,
  requestedDuration: number,
): number => {
  if (!isFinitePositive(totalDuration)) {
    return 0;
  }

  const fallbackDuration = totalDuration;
  const safeRequestedDuration = isFinitePositive(requestedDuration)
    ? requestedDuration
    : fallbackDuration;

  return clamp(safeRequestedDuration, minVisibleDuration, totalDuration);
};

const getViewportStart = (
  totalDuration: number,
  requestedStart: number,
  viewportDuration: number,
): number => {
  if (!isFinitePositive(totalDuration)) {
    return 0;
  }

  const maxStart = Math.max(0, totalDuration - viewportDuration);
  return clamp(requestedStart, 0, maxStart);
};

export interface UseTimelineViewportOptions {
  totalDuration: number;
  visibleWidth: number;
  currentTime: number;
  isMobile: boolean;
  autoFollowEnabled?: boolean;
  mobileDefaultWindowSeconds?: number;
  minVisibleDurationSeconds?: number;
  autoFollowIdleMs?: number;
}

export interface TimelineViewportState {
  viewportStart: number;
  viewportEnd: number;
  viewportDuration: number;
  zoomRatio: number;
  contentWidth: number;
  contentOffsetX: number;
  isZoomed: boolean;
  timeToX: (time: number) => number;
  xToTime: (x: number) => number;
  resetZoom: () => void;
  panByTime: (deltaTime: number) => void;
  panByPixels: (deltaPixels: number) => void;
  zoomAroundTime: (time: number, scale: number) => void;
  centerOnTime: (time: number) => void;
  markUserInteracted: () => void;
}

export function useTimelineViewport({
  totalDuration,
  visibleWidth,
  currentTime,
  isMobile,
  autoFollowEnabled = true,
  mobileDefaultWindowSeconds = MOBILE_DEFAULT_WINDOW_SECONDS,
  minVisibleDurationSeconds = MIN_VISIBLE_DURATION_SECONDS,
  autoFollowIdleMs = AUTO_FOLLOW_IDLE_MS,
}: UseTimelineViewportOptions): TimelineViewportState {
  const safeTotalDuration = isFinitePositive(totalDuration) ? totalDuration : 0;
  const safeVisibleWidth = isFinitePositive(visibleWidth) ? visibleWidth : 0;
  const safeCurrentTime = safeTotalDuration
    ? clamp(Number.isFinite(currentTime) ? currentTime : 0, 0, safeTotalDuration)
    : 0;

  const [viewportStart, setViewportStart] = useState(0);
  const [viewportDuration, setViewportDuration] = useState(0);
  const lastInteractionAtRef = useRef(0);
  const manualOverrideRef = useRef(false);
  const mobileViewportInitializedRef = useRef(false);
  const previousMobileDurationRef = useRef<number | null>(null);

  const minVisibleDuration = useMemo(() => {
    if (!safeTotalDuration) {
      return minVisibleDurationSeconds;
    }

    return Math.min(safeTotalDuration, minVisibleDurationSeconds);
  }, [minVisibleDurationSeconds, safeTotalDuration]);

  const clampViewportStart = useCallback((start: number, duration: number) => {
    return getViewportStart(safeTotalDuration, start, duration);
  }, [safeTotalDuration]);

  const applyViewport = useCallback((start: number, duration: number) => {
    if (!safeTotalDuration) {
      setViewportStart(0);
      setViewportDuration(0);
      return;
    }

    const nextDuration = getViewportDuration(safeTotalDuration, minVisibleDuration, duration);
    const nextStart = clampViewportStart(start, nextDuration);

    setViewportDuration(nextDuration);
    setViewportStart(nextStart);
  }, [clampViewportStart, minVisibleDuration, safeTotalDuration]);

  useEffect(() => {
    if (!safeTotalDuration) {
      setViewportStart(0);
      setViewportDuration(0);
      mobileViewportInitializedRef.current = false;
      previousMobileDurationRef.current = null;
      return;
    }

    const nextDuration = getViewportDuration(safeTotalDuration, minVisibleDuration, viewportDuration);
    const nextStart = clampViewportStart(viewportStart, nextDuration);

    if (nextDuration !== viewportDuration) {
      setViewportDuration(nextDuration);
    }

    if (nextStart !== viewportStart) {
      setViewportStart(nextStart);
    }
  }, [clampViewportStart, minVisibleDuration, safeTotalDuration, viewportDuration, viewportStart]);

  useEffect(() => {
    if (!isMobile || !safeTotalDuration) {
      if (!isMobile) {
        mobileViewportInitializedRef.current = false;
        previousMobileDurationRef.current = null;
      }
      return;
    }

    if (previousMobileDurationRef.current !== safeTotalDuration) {
      mobileViewportInitializedRef.current = false;
      previousMobileDurationRef.current = safeTotalDuration;
    }

    if (mobileViewportInitializedRef.current) {
      return;
    }

    mobileViewportInitializedRef.current = true;

    const initialDuration = Math.min(safeTotalDuration, mobileDefaultWindowSeconds);
    const initialStart = initialDuration < safeTotalDuration
      ? safeCurrentTime - (initialDuration / 2)
      : 0;

    applyViewport(initialStart, initialDuration);
  }, [
    applyViewport,
    isMobile,
    mobileDefaultWindowSeconds,
    safeCurrentTime,
    safeTotalDuration,
  ]);

  const zoomRatio = useMemo(() => {
    if (!safeTotalDuration || !viewportDuration) {
      return 1;
    }

    return safeTotalDuration / viewportDuration;
  }, [safeTotalDuration, viewportDuration]);

  const contentWidth = useMemo(() => {
    if (!safeVisibleWidth) {
      return 0;
    }

    return Math.max(safeVisibleWidth, safeVisibleWidth * zoomRatio);
  }, [safeVisibleWidth, zoomRatio]);

  const contentOffsetX = useMemo(() => {
    if (!safeTotalDuration || !contentWidth) {
      return 0;
    }

    return (viewportStart / safeTotalDuration) * contentWidth;
  }, [contentWidth, safeTotalDuration, viewportStart]);

  const viewportEnd = useMemo(() => {
    if (!safeTotalDuration) {
      return 0;
    }

    return Math.min(viewportStart + viewportDuration, safeTotalDuration);
  }, [safeTotalDuration, viewportDuration, viewportStart]);

  const markUserInteracted = useCallback(() => {
    lastInteractionAtRef.current = Date.now();
  }, []);

  const resetZoom = useCallback(() => {
    markUserInteracted();
    manualOverrideRef.current = false;
    applyViewport(0, safeTotalDuration || minVisibleDuration);
  }, [applyViewport, markUserInteracted, minVisibleDuration, safeTotalDuration]);

  const panByTime = useCallback((deltaTime: number) => {
    if (!viewportDuration || viewportDuration >= safeTotalDuration) {
      return;
    }

    markUserInteracted();
    manualOverrideRef.current = true;
    setViewportStart((previousStart) => clampViewportStart(previousStart + deltaTime, viewportDuration));
  }, [clampViewportStart, markUserInteracted, safeTotalDuration, viewportDuration]);

  const panByPixels = useCallback((deltaPixels: number) => {
    if (!safeVisibleWidth || !viewportDuration) {
      return;
    }

    panByTime((deltaPixels / safeVisibleWidth) * viewportDuration);
  }, [panByTime, safeVisibleWidth, viewportDuration]);

  const centerOnTime = useCallback((time: number) => {
    if (!safeTotalDuration || !viewportDuration) {
      return;
    }

    const targetTime = clamp(time, 0, safeTotalDuration);
    manualOverrideRef.current = false;
    setViewportStart(clampViewportStart(targetTime - (viewportDuration / 2), viewportDuration));
  }, [clampViewportStart, safeTotalDuration, viewportDuration]);

  const zoomAroundTime = useCallback((time: number, scale: number) => {
    if (!safeTotalDuration || !viewportDuration || !Number.isFinite(scale) || scale <= 0) {
      return;
    }

    const focusTime = clamp(time, 0, safeTotalDuration);
    const nextDuration = getViewportDuration(
      safeTotalDuration,
      minVisibleDuration,
      viewportDuration * scale,
    );
    const focusRatio = viewportDuration > 0
      ? clamp((focusTime - viewportStart) / viewportDuration, 0, 1)
      : 0.5;
    const nextStart = clampViewportStart(focusTime - (nextDuration * focusRatio), nextDuration);

    markUserInteracted();
    manualOverrideRef.current = true;
    setViewportDuration(nextDuration);
    setViewportStart(nextStart);
  }, [
    clampViewportStart,
    markUserInteracted,
    minVisibleDuration,
    safeTotalDuration,
    viewportDuration,
    viewportStart,
  ]);

  const timeToX = useCallback((time: number) => {
    if (!safeTotalDuration || !contentWidth) {
      return 0;
    }

    const clampedTime = clamp(time, 0, safeTotalDuration);
    return ((clampedTime / safeTotalDuration) * contentWidth) - contentOffsetX;
  }, [contentOffsetX, contentWidth, safeTotalDuration]);

  const xToTime = useCallback((x: number) => {
    if (!safeTotalDuration || !contentWidth) {
      return 0;
    }

    return clamp(((x + contentOffsetX) / contentWidth) * safeTotalDuration, 0, safeTotalDuration);
  }, [contentOffsetX, contentWidth, safeTotalDuration]);

  useEffect(() => {
    if (!autoFollowEnabled || !safeTotalDuration || viewportDuration <= 0 || viewportDuration >= safeTotalDuration) {
      return;
    }

    if (manualOverrideRef.current) {
      return;
    }

    if (Date.now() - lastInteractionAtRef.current < autoFollowIdleMs) {
      return;
    }

    const leadingEdge = viewportStart + (viewportDuration * AUTO_FOLLOW_PADDING_RATIO);
    const trailingEdge = viewportEnd - (viewportDuration * AUTO_FOLLOW_PADDING_RATIO);

    if (safeCurrentTime < leadingEdge || safeCurrentTime > trailingEdge) {
      setViewportStart(clampViewportStart(safeCurrentTime - (viewportDuration / 2), viewportDuration));
    }
  }, [
    autoFollowIdleMs,
    autoFollowEnabled,
    clampViewportStart,
    safeCurrentTime,
    safeTotalDuration,
    viewportDuration,
    viewportEnd,
    viewportStart,
  ]);

  return {
    viewportStart,
    viewportEnd,
    viewportDuration,
    zoomRatio,
    contentWidth,
    contentOffsetX,
    isZoomed: viewportDuration > 0 && viewportDuration < safeTotalDuration,
    timeToX,
    xToTime,
    resetZoom,
    panByTime,
    panByPixels,
    zoomAroundTime,
    centerOnTime,
    markUserInteracted,
  };
}
